<template>
  <div class="container">
    <div class="row">
      <div class="col-12">
        <b-tabs
          content-class="mt-3"
          active-nav-item-class="font-weight-bold"
          v-model="currentTab"
        >
          <b-tab title="Form" active>
            <b-form
              class="p-2"
              @submit.prevent="onSubmit"
              :method="'POST'"
              :action="route"
            >
              <input type="hidden" name="_token" :value="csrf" />
              <input type="hidden" name="_method" :value="method" />

              <common-loading
                :message="$t('messages.fetching_message')"
                v-if="states.fetching"
              />

              <b-alert
                :show="
                  messageId && !(permissions.indexOf('message-edit') !== -1)
                "
                variant="warning"
              >
                <em class="fa fa-info-circle mr-2"></em>
                {{ $t("commons.read_only") }}
              </b-alert>

              <div class="row">
                <div class="col-lg-6 pr-3 pl-3">
                  <b-form-group
                    id="fieldset-name"
                    label-for="name"
                    label-size="lg"
                    label-class="font-weight-bold pb-0"
                    class="required"
                  >
                    <template slot="label">
                      <span v-text="$t('messages.name')" />
                    </template>
                    <b-form-input
                      id="name"
                      name="name"
                      v-model.trim="name"
                      :placeholder="$t('messages.enter_name')"
                      :disabled="disableForm"
                    />
                    <div v-if="hasError('name')">
                      <div
                        class="alert alert-danger mt-1"
                        v-if="states.showErrors"
                        v-text="$t('commons.required_field_error')"
                      ></div>
                    </div>
                  </b-form-group>
                </div>
                <div class="col-lg-6 pr-3">
                  <b-form-group
                    id="fieldset-id"
                    label-for="id"
                    label-size="lg"
                    label-class="font-weight-bold pb-0"
                  >
                    <template slot="label">
                      <span v-text="$t('messages.id')" />
                    </template>
                    <b-form-input
                      id="id"
                      name="id"
                      v-model.trim="id"
                      maxlength="200"
                      :placeholder="$t('messages.enter_id')"
                      :disabled="disableForm || messageId != null"
                    />
                  </b-form-group>
                </div>
              </div>

              <div class="row">
                <div class="col-lg-12 p-3">
                  <b-form-group
                    id="fieldset-description"
                    label-for="description"
                    label-size="lg"
                    label-class="font-weight-bold pb-0"
                    class="required"
                  >
                    <template slot="label">
                      <span v-text="$t('messages.description')" />
                    </template>
                    <b-form-input
                      id="description"
                      name="description"
                      v-model.trim="description"
                      :placeholder="$t('messages.enter_description')"
                      :disabled="disableForm"
                    />
                    <div v-if="hasError('description')">
                      <div
                        class="alert alert-danger mt-1"
                        v-if="states.showErrors"
                        v-text="$t('commons.required_field_error')"
                      ></div>
                    </div>
                  </b-form-group>
                </div>
              </div>

              <div class="row">
                <div class="col-lg-6 pr-3 pl-3">
                  <b-form-group
                    label-for="type"
                    label-size="lg"
                    label-class="font-weight-bold pb-0"
                    class="required"
                  >
                    <template slot="label">
                      <span v-text="$t('messages.type')"></span>
                    </template>
                    <b-form-select
                      id="type"
                      name="type"
                      v-model.trim="type"
                      :options="types"
                      :disabled="disableForm"
                    >
                    </b-form-select>
                    <div v-if="hasError('type')">
                      <div
                        class="alert alert-danger mt-1"
                        v-if="states.showErrors"
                        v-text="$t('commons.required_field_error')"
                      ></div>
                    </div>
                  </b-form-group>
                </div>
                <div
                  v-if="messageId == null || (gcma != null && gcma != '')"
                  class="col-lg-6 pr-3"
                >
                  <b-form-group
                    id="fieldset-enter-gcma"
                    label-for="gcma"
                    label-size="lg"
                    label-class="font-weight-bold pb-0"
                  >
                    <template slot="label">
                      <span v-text="$t('messages.gcma')" />
                    </template>
                    <b-form-input
                      id="gcma"
                      name="gcma"
                      v-model.trim="gcma"
                      :placeholder="$t('messages.enter_gcma')"
                      :disabled="disableForm || messageId != null"
                    />
                  </b-form-group>
                </div>
              </div>

              <div v-if="messageId != null && reason != null" class="row">
                <div class="col-lg-12 p-3">
                  <b-form-group
                    id="fieldset-reason"
                    label-for="reason"
                    label-size="lg"
                    label-class="font-weight-bold pb-0"
                  >
                    <template slot="label">
                      <span v-text="$t('messages.reason')" />
                    </template>
                    <b-form-input
                      id="reason"
                      name="reason"
                      v-model.trim="reason"
                      :placeholder="$t('messages.reason')"
                      disabled
                    />
                  </b-form-group>
                </div>
              </div>

              <div class="row">
                <div class="col-lg-12">
                  <b-form-group
                    id="fieldset-message-template"
                    label-for="message_template"
                    label-size="lg"
                    label-class="font-weight-bold pb-0"
                  >
                    <template slot="label">
                      <span v-text="$t('messages.message_template')" />
                      <span class="text-success" v-if="status == 'APPROVED'">{{
                        status
                      }}</span>
                      <span class="text-info" v-if="status == 'DRAFT'">{{
                        status
                      }}</span>
                      <span class="text-danger" v-if="status == 'REJECTED'">{{
                        status
                      }}</span>
                    </template>
                    <b-form-textarea
                      id="textarea"
                      v-model="message"
                      :disabled="disableForm"
                      :placeholder="$t('messages.enter_mt_text')"
                      rows="3"
                      max-rows="6"
                    ></b-form-textarea>
                    <div v-if="hasError('message')">
                      <div
                        class="alert alert-danger mt-1"
                        v-if="states.showErrors"
                        v-text="$t('commons.required_field_error')"
                      ></div>
                    </div>
                  </b-form-group>
                </div>
              </div>

              <div class="row">
                <div class="col-lg-6 pr-3 pl-3">
                  <b-form-group
                    label-for="messageType"
                    label-size="lg"
                    label-class="font-weight-bold pb-0"
                    class="required"
                  >
                    <template slot="label">
                      <span v-text="$t('messages.messageType')"></span>
                    </template>
                    <b-form-select
                      id="messageType"
                      name="messageType"
                      v-model.trim="messageType"
                      :options="message_types"
                      :disabled="disableForm"
                    >
                    </b-form-select>
                    <div
                      v-if="
                        hasError('messageType') &&
                        (Object.keys(attributes).length === 0 ||
                          Object.keys(attributes.assets).length === 0)
                      "
                    >
                      <div
                        class="alert alert-danger mt-1"
                        v-if="states.showErrors"
                        v-text="$t('messages.incomplete_message')"
                      ></div>
                    </div>
                    <div v-if="hasError('messageType') && messageType != 'mms'">
                      <div
                        class="alert alert-danger mt-1"
                        v-if="states.showErrors"
                        v-text="$t('commons.required_field_error')"
                      ></div>
                    </div>
                  </b-form-group>
                </div>
                <div
                  v-if="
                    messageType == 'mms' && Object.keys(attributes).length === 0
                  "
                  class="col-lg-6 pr-3 mt-5"
                >
                  <span
                    class="text-danger font-weight-bold"
                    v-text="$t('messages.no_images')"
                  ></span>
                </div>
              </div>

              <b-form-group
                v-if="messageType == 'mms'"
                label-for="attributes"
                label-size="lg"
                label-class="font-weight-bold pb-0"
              >
                <template slot="label">
                  <span v-text="$t('commons.attributes')" />
                </template>

                <attributes-list></attributes-list>
              </b-form-group>

              <div class="row">
                <div class="col-lg-12">
                  <b-form-group
                    v-if="messageId != null"
                    id="fieldset-active"
                    label-for="active"
                    label-size="lg"
                    label-class="font-weight-bold pb-0"
                  >
                    <template slot="label">
                      <span v-text="$t('commons.active')" />
                    </template>

                    <toggle-button
                      v-model="isActive"
                      :labels="{ checked: 'On', unchecked: 'Off' }"
                      :sync="true"
                    />
                  </b-form-group>
                </div>
              </div>

              <b-form-group class="mt-3">
                <b-button
                  type="submit"
                  variant="primary"
                  v-text="save_or_update"
                  :disabled="
                    ((form.status == 'APPROVED' || form.status == 'REJECTED') &&
                      approvalProcess()) ||
                    disableForm ||
                    !(
                      permissions.indexOf('message-edit') !== -1 &&
                      permissions.indexOf('message-read') !== -1
                    )
                  "
                ></b-button>
                <b-button
                  v-if="
                    messageId != null &&
                    permissions.indexOf('content-approve') !== -1 &&
                    show_approve &&
                    isApprover()
                  "
                  @click="confirmChangeStatus('approve')"
                  variant="secondary"
                  v-text="approve_btn"
                  :disabled="
                    disableForm ||
                    !(
                      permissions.indexOf('message-edit') !== -1 &&
                      permissions.indexOf('message-read') !== -1
                    )
                  "
                ></b-button>

                <b-button
                  v-if="
                    messageId != null &&
                    permissions.indexOf('content-approve') !== -1 &&
                    show_reject &&
                    form.status != 'APPROVED' &&
                    isApprover()
                  "
                  @click="confirmChangeStatus('reject')"
                  variant="secondary"
                  v-text="reject_btn"
                  :disabled="
                    disableForm ||
                    !(
                      permissions.indexOf('message-edit') !== -1 &&
                      permissions.indexOf('message-read') !== -1
                    )
                  "
                ></b-button>

                <b-button
                  v-if="
                    messageId != null &&
                    permissions.indexOf('content-approve') !== -1 &&
                    show_draft &&
                    isApprover()
                  "
                  @click="confirmChangeStatus('draft')"
                  variant="secondary"
                  v-text="draft_btn"
                  :disabled="
                    disableForm ||
                    !(
                      permissions.indexOf('message-edit') !== -1 &&
                      permissions.indexOf('message-read') !== -1
                    )
                  "
                ></b-button>

                <b-button
                  class="btn btn-danger"
                  v-text="$t('buttons.cancel')"
                  :href="$routes('program.messages.index')"
                  :disabled="disableCancel"
                ></b-button>

               <b-button
                 variant="success"
                 v-if="messageId != null && permissions.indexOf('message-send') !== -1"
                 :disabled="
                   states.deleting ||
                   !(permissions.indexOf('message-send') !== -1)
                 "
                 @click="openMessageModal()"
                 class="ml-3"
               >
                 <span v-text="$t('buttons.send_sample_message')"></span>
                 <i class="fa fa-envelope"></i>
               </b-button>
              </b-form-group>

              <common-loading
                :message="$t('messages.saving')"
                v-if="states.saving"
              />
              <common-loading
                :message="$t('messages.approving')"
                v-if="states.change_message_status && action == 'approve'"
              />
              <common-loading
                :message="$t('messages.rejecting')"
                v-if="states.change_message_status && action == 'reject'"
              />
              <common-loading
                :message="$t('messages.drafting')"
                v-if="states.change_message_status && action == 'draft'"
              />
              <b-alert
                variant="danger"
                :show="states.showErrors && hasErrors"
                v-text="$t('commons.form_error')"
              />
              <b-alert
                variant="success"
                :show="states.showSuccess"
                v-text="$t('messages.saved_successfully')"
              />
              <b-alert
                  variant="success"
                  :show="states.showUrlShortenedSuccess"
                  v-text="$t('messages.url_shortened_successfully')"
              />

              <b-alert variant="danger" :show="errorMessage != null">
                <em class="fa fa-times-circle mr-2"></em>
                <span v-html="errorMessage"></span>
              </b-alert>
            </b-form>

            <change-status-modal ref="changeStatusModal"></change-status-modal>
            <message-update-modal
              :message-id="messageId"
              ref="messageUpdateModal"
            ></message-update-modal>
            <message-sample-modal
              :message-id="messageId"
              ref="messageSampleModal"
            ></message-sample-modal>
          </b-tab>
          <b-tab title="History" v-if="messageId != null" lazy>
            <b-card-text>
              <show-history
                :id="messageId"
                :instance-id="instanceId"
                :callback="MessagesApi.find"
              ></show-history>
            </b-card-text>
          </b-tab>
        </b-tabs>
      </div>
    </div>
  </div>
</template>

<script>
import MessagesApi from "../../../apis/MessagesApi";
import AttributeUtils from "../../../utility/attributeUtils";
import { useManageAttributes } from "../../../stores/manage-attributes";

export default {
  props: {
    messageId: String,
    permissions: {
      type: Array,
    },
    userEmail: String,
  },
  computed: {
    form() {
      return this.useManageAttributes.getForm;
    },
    id: {
      get() {
        return this.useManageAttributes.getForm.id;
      },
      set(value) {
        this.useManageAttributes.updateValue({
          key: "id",
          value: value,
        });
      },
    },
    name: {
      get() {
        return this.useManageAttributes.getForm.name;
      },
      set(value) {
        this.useManageAttributes.updateValue({
          key: "name",
          value: value,
        });
      },
    },
    description: {
      get() {
        return this.useManageAttributes.getForm.description;
      },
      set(value) {
        this.useManageAttributes.updateValue({
          key: "description",
          value: value,
        });
      },
    },
    type: {
      get() {
        return this.useManageAttributes.getForm.type;
      },
      set(value) {
        this.useManageAttributes.updateValue({
          key: "type",
          value: value,
        });
      },
    },
    gcma: {
      get() {
        return this.useManageAttributes.getForm.gcma;
      },
      set(value) {
        this.useManageAttributes.updateValue({
          key: "gcma",
          value: value,
        });
      },
    },
    status: {
      get() {
        return this.useManageAttributes.getForm.status;
      },
      set(value) {
        this.useManageAttributes.updateValue({
          key: "status",
          value: value,
        });
      },
    },
    reason: {
      get() {
        return this.useManageAttributes.getForm.reason;
      },
      set(value) {
        this.useManageAttributes.updateValue({
          key: "reason",
          value: value,
        });
      },
    },
    message: {
      get() {
        return this.useManageAttributes.getForm.message;
      },
      set(value) {
        this.useManageAttributes.updateValue({
          key: "message",
          value: value,
        });
      },
    },
    messageType: {
      get() {
        return this.useManageAttributes.getForm.messageType;
      },
      set(value) {
        this.useManageAttributes.updateValue({
          key: "messageType",
          value: value,
        });
      },
    },
    isActive: {
      get() {
        return this.useManageAttributes.getForm.isActive;
      },
      set(value) {
        this.useManageAttributes.updateValue({
          key: "isActive",
          value: value,
        });
      },
    },
    createTime: {
      get() {
        return new Date(
          this.useManageAttributes.getForm.createTime
        ).toLocaleString();
      },
    },
    lastModifiedTime: {
      get() {
        return new Date(
          this.useManageAttributes.getForm.lastModifiedTime
        ).toLocaleString();
      },
    },
    createdBy: {
      get() {
        return this.useManageAttributes.getForm.createdBy;
      },
    },
    states() {
      return this.useManageAttributes.states;
    },
    errors() {
      return this.useManageAttributes.errors;
    },
    hasErrors() {
      return this.useManageAttributes.hasErrors;
    },
    disableForm() {
      return this.useManageAttributes.disableForm;
    },
    disableCancel() {
      return this.useManageAttributes.disableCancel;
    },
    attributes: {
      get() {
        return this.useManageAttributes.getForm.attributes;
      },
      set(attributes) {
        this.useManageAttributes.updateAttributes(attributes);
      },
    },
    errorMessage() {
      return this.useManageAttributes.errorMessage;
    },
  },
  data() {
    return {
      instanceId: window.Laravel.selected_instance
        ? window.Laravel.selected_instance.id
        : null,
      csrf: null,
      route: null,
      method: "POST",
      save_or_update: this.$t("buttons.save"),
      approve_or_reject: this.$t("buttons.approve"),
      options: { mode: "preview", mainMenuBar: false, statusBar: true },
      default_attribute: {
        template: {},
      },
      currentTab: 0,
      MessagesApi: MessagesApi,
      types: [
        { value: null, text: this.$t("messages.type") },
        { value: "inbound", text: "Inbound" },
        { value: "outbound", text: "Outbound" },
      ],
      message_types: [
        { value: null, text: this.$t("messages.messageType") },
        { value: "sms", text: "SMS" },
        { value: "mms", text: "MMS" },
      ],
      show_approve: false,
      show_reject: false,
      show_draft: false,
      approve_btn: this.$t("buttons.approve"),
      reject_btn: this.$t("buttons.reject"),
      draft_btn: this.$t("buttons.draft"),
      action: null,
      useManageAttributes: useManageAttributes(),
    };
  },
  created() {
    this.initForm();
    this.useManageAttributes.updateStates({
      showJsonEditor: false,
      showAttributeValues: true,
      allowBase64EncDec: true,
    });

    this.useManageAttributes.setDataTypes([
      {
        type: "Image",
        disabled: false,
      },
    ]);
    this.useManageAttributes.updateErrors({
      name: false,
      description: false,
      attributes: false,
      status: false,
      type: false,
      messageType: false,
      message: false,
    });

    this.useManageAttributes.setPermissions(this.permissions);
    this.useManageAttributes.setDocumentId(this.messageId);

    this.csrf = document.head.querySelector('meta[name="csrf-token"]').content;

    if (this.messageId) {
      (this.save_or_update = this.$t("buttons.update")), this.fetchMessage();
      this.useManageAttributes.updateTemplate(this.default_attribute);
      this.useManageAttributes.setDefaultDataType("Image");
    } else {
      this.useManageAttributes.setDefaultDataType("Image");
    }
  },
  methods: {
    isApprover() {
      const approvers =
        window.Laravel?.selected_instance?.attributes?.messageTemplate?.approvers ?? [];
      return Array.isArray(approvers) && approvers.includes(this.userEmail);
    },
    openMessageModal() {
      this.$refs.messageSampleModal.open();
    },
    approvalProcess() {
      return (
        window.Laravel.selected_instance.hasOwnProperty("attributes") &&
        window.Laravel.selected_instance.attributes.hasOwnProperty(
          "messageTemplate"
        ) &&
        window.Laravel.selected_instance.attributes.messageTemplate.hasOwnProperty(
          "approvalProcessEnabled"
        ) &&
        window.Laravel.selected_instance.attributes.messageTemplate
          .approvalProcessEnabled
      );
    },
    initForm() {
      let formData = {
        result: {
          id: null,
          name: null,
          description: null,
          isActive: null,
          status: null,
          type: null,
          messageType: null,
          message: null,
          reason: null,
          gcma: null,
        },
      };
      this.useManageAttributes.initialize(formData);
    },
    onSubmit(e) {
      this.useManageAttributes.updateStates({
        showErrors: true,
      });
      if (this.hasErrors) {
        this.$toast.error("Error", this.$t("commons.form_error"));
        return;
      }
      if (this.messageId) {
        this.form.messageType == "sms"
          ? this.updateMessage()
          : this.saveOrUpdateMessage();
      } else {
        this.form.messageType == "sms"
          ? this.createMessage()
          : this.saveOrUpdateMessage();
      }
    },
    hasError(field) {
      if (
        !this.form[field] ||
        this.form[field].length == 0 ||
        (this.form[field] == "mms" &&
          (Object.keys(this.attributes).length === 0 ||
            (this.attributes.assets &&
              Object.keys(this.attributes.assets).length === 0)))
      ) {
        this.useManageAttributes.updateErrors({ [field]: true });
        return true;
      } else {
        this.useManageAttributes.updateErrors({ [field]: false });
        return false;
      }
    },
    fetchMessage() {
      this.useManageAttributes.updateStates({
        fetching: true,
      });
      MessagesApi.find(this.messageId, this.instanceId)
        .then((r) => {
          this.useManageAttributes.updateStates({
            fetching: false,
          });
          this.useManageAttributes.initialize(r.data);
          if (this.form.status == "REJECTED") {
            this.show_approve = this.show_reject = false;
            this.show_draft = true;
          } else if (this.form.status == "DRAFT") {
            this.show_approve = this.show_reject = true;
            this.show_draft = false;
          } else if (this.form.status == "APPROVED") {
            this.show_approve = this.show_reject = this.show_draft = false;
          } else {
            this.show_draft = true;
          }

          // Set the Data Type and disable the field.
          this.useManageAttributes.setAttributeDataType({
            name: "executeScript",
            dataType: "Text",
          });
          this.useManageAttributes.toggleAttributeState({
            name: "executeScript",
            disableUpdate: true,
          });

          this.useManageAttributes.updateHeaders(r.headers);
        })
        .catch((e) => {
          this.useManageAttributes.updateStates({
            fetching: false,
          });
          this.$toast.error("Error", "Unable to fetch message");
        });
    },
    confirmChangeStatus(action) {
      this.action = action;
      this.$refs.changeStatusModal.open(action).then((response) => {
        if (response) {
          this.changeStatus(response);
        }
      });
    },
    changeStatus(reason) {
      this.useManageAttributes.updateStates({
        change_message_status: true,
        showSuccess: false,
      });
      this.useManageAttributes.setErrorMessage(null);
      let eTag = null;
      let headers = this.useManageAttributes.headers;
      if (headers && headers["ETag"] && headers["ETag"].length > 0) {
        eTag = headers["ETag"][0];
      }

      let status = "DRAFT";
      if (this.action == "approve") {
        status = "APPROVED";
      }
      if (this.action == "reject") {
        status = "REJECTED";
      }

      let payload = {
        payload: {
          id: this.form.id,
          name: this.form.name,
          description: this.form.description,
          isActive: this.form.isActive,
          status: status,
          attributes: this.form.attributes,
        },
        eTag: eTag,
      };
      payload.payload.attributes.reason = reason;
      payload.payload.attributes.message = this.form.message;
      payload.payload.attributes.messageType = this.form.messageType;
      payload.payload.attributes.gcma = this.form.gcma;
      payload.payload.attributes.type = this.form.type;
      MessagesApi.update(payload, this.instanceId, this.messageId)
        .then((r) => {
          this.useManageAttributes.updateStates({
            change_message_status: false,
          });
          this.$toast.success(
            "Success",
            this.$t("messages.status_changed_successfully")
          );
          window.location.href = this.$routes("program.messages.edit", {
            id: this.form.id,
          });
        })
        .catch((e) => {
          this.useManageAttributes.updateStates({
            saving: false,
          });
          if (e.response && e.response.status == 409) {
            this.$toast.error("Error", this.$t("messages.already_exists"));
            this.useManageAttributes.setErrorMessage(
              this.$t("messages.already_exists")
            );
          } else if (e.response.status == 400) {
            this.$toast.error("Error", e.response.data.data.detail);
            this.useManageAttributes.setErrorMessage(
              e.response.data.data.detail
            );
          } else if (e.response.status == 422) {
            let err = AttributeUtils.convertToErrorMessage(
              e.response.data.errors
            );
            this.$toast.error("Error", err);
            this.useManageAttributes.setErrorMessage(err);
          } else {
            this.$toast.error("Error", this.$t("messages.create_failed"));
            this.useManageAttributes.setErrorMessage(
              this.$t("messages.create_failed")
            );
          }
        });
    },
    createMessage() {
      this.useManageAttributes.updateStates({
        saving: true,
        showSuccess: false,
        showUrlShortenedSuccess: false,
      });
      this.useManageAttributes.setErrorMessage(null);
      let attrs = {
        message: this.form.message,
        type: this.form.type,
        messageType: this.form.messageType,
        gcma: this.form.gcma,
      };
      let payload = {
        payload: {
          name: this.form.name,
          description: this.form.description,
          isActive: true,
          status: this.approvalProcess() ? "DRAFT" : "APPROVED",
          attributes: attrs,
        },
      };
      if (this.form.id) {
        payload.payload.id = this.form.id;
      }
      MessagesApi.create(payload, this.instanceId)
        .then((r) => {
          let states = {
            showSuccess: true,
          };

          if (r.headers["x-url-shortened"] !== undefined) {
            states.showUrlShortenedSuccess = true;
          }

          this.useManageAttributes.updateStates(states);
          this.$toast.success(
            "Success",
            this.$t("messages.saved_successfully")
          );
          window.location.href = this.$routes("program.messages.edit", {
            id: r.data.result.id,
          });
        })
        .catch((e) => {
          this.useManageAttributes.updateStates({
            saving: false,
          });
          if (e.response && e.response.status == 409) {
            this.$toast.error("Error", this.$t("messages.already_exists"));
            this.useManageAttributes.setErrorMessage(
              this.$t("messages.already_exists")
            );
          } else if (e.response && e.response.status == 400) {
            this.$toast.error("Error", e.response.data.data.detail);
            this.useManageAttributes.setErrorMessage(
              e.response.data.data.detail
            );
          } else if (e.response && e.response.status == 422) {
            let err = AttributeUtils.convertToErrorMessage(
              e.response.data.errors
            );
            this.$toast.error("Error", err);
            this.useManageAttributes.setErrorMessage(err);
          } else {
            this.$toast.error("Error", this.$t("messages.create_failed"));
            this.useManageAttributes.setErrorMessage(
              this.$t("messages.create_failed")
            );
          }
        });
    },
    saveOrUpdateMessage() {
      this.useManageAttributes.updateStates({
        saving: true,
        showSuccess: false,
      });
      this.$refs.messageUpdateModal.open();
    },
    updateMessage() {
      this.useManageAttributes.updateStates({
        saving: true,
        showSuccess: false,
        showUrlShortenedSuccess: false,
      });
      this.useManageAttributes.setErrorMessage(null);

      let eTag = null;
      let headers = this.useManageAttributes.headers;
      if (headers && headers["ETag"] && headers["ETag"].length > 0) {
        eTag = headers["ETag"][0];
      }
      this.useManageAttributes.updateMessageTemplate({
        key: "message",
        value: this.form.message,
      });
      this.useManageAttributes.updateMessageTemplate({
        key: "messageType",
        value: this.form.messageType,
      });
      this.useManageAttributes.updateMessageTemplate({
        key: "type",
        value: this.form.type,
      });
      this.useManageAttributes.updateMessageTemplate({
        key: "gcma",
        value: this.form.gcma,
      });
      this.useManageAttributes.updateMessageTemplate({
        key: "reason",
        value: this.form.reason,
      });

      this.form.attributesList = [];
      if ("assets" in this.form.attributes) {
        delete this.form.attributes.assets;
      }
      let attr = {
        message: this.form.message,
        type: this.form.type,
        messageType: this.form.messageType,
        gcma: this.form.gcma,
        reason: this.form.reason,
      };

      let payload = {
        payload: {
          id: this.form.id,
          name: this.form.name,
          description: this.form.description,
          isActive: this.form.isActive,
          status: this.form.status,
          attributes: attr,
        },
        eTag: eTag,
      };

      MessagesApi.update(payload, this.instanceId, this.messageId)
        .then((r) => {
          let states = {
            showSuccess: true,
          };

          if (r.headers["x-url-shortened"] !== undefined) {
            states.showUrlShortenedSuccess = true;
          }

          this.useManageAttributes.updateStates(states);
          this.$toast.success(
            "Success",
            this.$t("messages.saved_successfully")
          );
          this.useManageAttributes.initialize(r.data);
          this.useManageAttributes.updateHeaders(r.headers);
          this.useManageAttributes.updateStates({
            saving: false,
            showSuccess: true,
          });
          // Set the Data Type and disable the field.
          this.useManageAttributes.setAttributeDataType({
            name: "executeScript",
            dataType: "Text",
          });
          this.useManageAttributes.toggleAttributeState({
            name: "executeScript",
            disableUpdate: true,
          });
        })
        .catch((e) => {
          let errorMessage = this.$t("messages.update_failed");
          this.useManageAttributes.updateStates({
            saving: false,
          });

          if (e.response.status == 412) {
            errorMessage = this.$t("commons.optimistic_lock");
            this.useManageAttributes.setErrorMessage(errorMessage);
            this.invalid = true;
          } else if (e.response.status == 400) {
            this.$toast.error("Error", e.response.data.data.detail);
            this.useManageAttributes.setErrorMessage(
              e.response.data.data.detail
            );
          }

          this.$toast.error("Error", errorMessage);
        });
    },
  },
};
</script>
